import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  HelpCircle,
  Loader2,
  AlertCircle,
  Mail,
  ShieldCheck,
  Clock,
} from 'lucide-react';
import { isPlausiblePublicToken, fetchWithTimeout } from '@core/utils/publicToken';
import { B2BLandingHeader } from '@modules/public/landing/B2BLandingHeader';
import { B2BFooter } from '@modules/public/landing/B2BTestimonialsAndCTA';

type CTAAction = 'accept' | 'reject' | 'query';
type PageState = 'loading' | 'ready' | 'submitting' | 'done' | 'error' | 'already_responded';

interface ThreadInfo {
  candidate_name: string;
  job_title: string;
  org_name: string;
  cta_response: CTAAction | null;
}

interface CtaOption {
  action: CTAAction;
  label: string;
  description: string;
  icon: React.ReactNode;
  /** Tailwind classes applied when the option is selected */
  selected: string;
  /** Accent colour for the icon circle */
  accent: string;
}

const CTA_RESPONSE_LABEL: Record<CTAAction, string> = {
  accept: 'Shortlisted',
  reject: 'Passed',
  query: 'Query submitted',
};

export function RespondPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const defaultAction = searchParams.get('action') as CTAAction | null;

  const [state, setState] = useState<PageState>('loading');
  const [thread, setThread] = useState<ThreadInfo | null>(null);
  const [selected, setSelected] = useState<CTAAction | null>(defaultAction);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    if (!isPlausiblePublicToken(token)) {
      setState('error');
      setError('This link does not look valid. Please use the link from your email or ask your recruiter for a new one.');
      return;
    }
    const headers = {
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    };
    fetchWithTimeout(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ggj_b2b_process-cta-response?token=${encodeURIComponent(token)}&mode=info`,
      { headers, timeoutMs: 25_000 },
    )
      .then(async (r) => ({ r, data: (await r.json()) as { error?: string; cta_response?: CTAAction | null } }))
      .then(async ({ r, data }) => {
        if (r.status === 410) {
          setState('error');
          setError('This link has expired. Please ask your recruiter to send a fresh email.');
          return;
        }
        if (data.error) {
          setState('error');
          setError(data.error);
          return;
        }
        if (data.cta_response) {
          setState('already_responded');
          setThread(data as ThreadInfo);
          return;
        }
        setThread(data as ThreadInfo);
        if (defaultAction === 'accept' || defaultAction === 'reject') {
          setState('submitting');
          const res = await fetchWithTimeout(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ggj_b2b_process-cta-response`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
                Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({ token, action: defaultAction }),
              timeoutMs: 25_000,
            },
          );
          const result = await res.json();
          if (res.status === 410) {
            setState('error');
            setError('This link has expired. Please ask your recruiter to send a fresh email.');
            return;
          }
          if (res.status === 409 && result.cta_response) {
            setThread({ ...(data as ThreadInfo), cta_response: result.cta_response });
            setState('already_responded');
            return;
          }
          if (result.error) {
            setState('error');
            setError(result.error);
            return;
          }
          setState('done');
          return;
        }
        setState('ready');
      })
      .catch(() => {
        setState('error');
        setError('Unable to load response form. Please try again.');
      });
  }, [token, defaultAction]);

  const handleSubmit = async () => {
    if (!selected || !token) return;
    setState('submitting');
    try {
      const res = await fetchWithTimeout(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ggj_b2b_process-cta-response`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ token, action: selected, note: note || undefined }),
          timeoutMs: 25_000,
        },
      );
      const data = await res.json();
      if (res.status === 410) {
        setState('error');
        setError('This link has expired. Please ask your recruiter to send a fresh email.');
        return;
      }
      if (res.status === 409 && data.cta_response) {
        setThread({ ...thread!, cta_response: data.cta_response });
        setState('already_responded');
        return;
      }
      if (data.error) throw new Error(data.error);
      setState('done');
    } catch (err: unknown) {
      setState('error');
      setError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    }
  };

  const CTA_OPTIONS: CtaOption[] = [
    {
      action: 'accept',
      label: 'Shortlist candidate',
      description: 'I want to move forward with this candidate.',
      icon: <CheckCircle className="h-5 w-5" />,
      selected:
        'border-emerald-500 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-200 dark:border-emerald-400 dark:bg-emerald-950/40 dark:text-emerald-100 dark:ring-emerald-900/40',
      accent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
    },
    {
      action: 'reject',
      label: 'Pass',
      description: 'This candidate is not the right fit at this time.',
      icon: <XCircle className="h-5 w-5" />,
      selected:
        'border-rose-500 bg-rose-50 text-rose-900 ring-2 ring-rose-200 dark:border-rose-400 dark:bg-rose-950/40 dark:text-rose-100 dark:ring-rose-900/40',
      accent: 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300',
    },
    {
      action: 'query',
      label: 'I have a query',
      description: 'I need more information before deciding.',
      icon: <HelpCircle className="h-5 w-5" />,
      selected:
        'border-amber-500 bg-amber-50 text-amber-900 ring-2 ring-amber-200 dark:border-amber-400 dark:bg-amber-950/40 dark:text-amber-100 dark:ring-amber-900/40',
      accent: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-950">
      <B2BLandingHeader forceSolid hideDemoButton />
      <div className="h-[75px] flex-shrink-0" />

      <main className="relative flex flex-1 flex-col">
        {/* Soft brand wash behind the card */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-80"
          style={{
            background:
              'radial-gradient(60% 80% at 50% 0%, rgba(141,134,255,0.22) 0%, rgba(141,134,255,0) 60%)',
          }}
        />

        <div className="relative mx-auto w-full max-w-3xl px-4 pb-16 pt-8 sm:pt-12">
          {/* Page heading */}
          <div className="mb-6 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-indigo-600 dark:text-indigo-400">
              Candidate response
            </p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl dark:text-white">
              Let the recruiter know your decision
            </h1>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-gray-500 dark:text-gray-400">
              Your response is recorded instantly and notifies the recruiter — this link is
              single-use and expires for your protection.
            </p>
          </div>

          {/* Main card */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_12px_40px_-8px_rgba(15,23,42,0.08)] dark:border-gray-800 dark:bg-gray-900">
            {/* Status strip */}
            <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-indigo-50/60 to-violet-50/60 px-5 py-3 dark:border-gray-800 dark:from-indigo-950/20 dark:to-violet-950/20">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-white shadow-sm">
                  <Mail className="h-3.5 w-3.5" />
                </span>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  Candidate submission
                </span>
              </div>
              <div className="hidden items-center gap-1 text-xs font-medium text-gray-500 sm:flex dark:text-gray-400">
                <Clock className="h-3.5 w-3.5" /> Awaiting your action
              </div>
            </div>

            <div className="p-5 sm:p-8">
              {state === 'loading' && (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                </div>
              )}

              {state === 'error' && (
                <div className="py-8 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-900/40">
                    <AlertCircle className="h-6 w-6 text-rose-600 dark:text-rose-300" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Something went wrong
                  </h2>
                  <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
                    {error}
                  </p>
                </div>
              )}

              {state === 'already_responded' && thread && (
                <div className="py-8 text-center">
                  <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
                    <CheckCircle className="h-7 w-7 text-emerald-600 dark:text-emerald-300" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Response received — thank you
                  </h2>
                  <p className="mx-auto mt-2 max-w-md text-sm text-gray-600 dark:text-gray-300">
                    You&apos;ve already responded with{' '}
                    <strong className="text-emerald-600 dark:text-emerald-400">
                      {CTA_RESPONSE_LABEL[thread.cta_response!]}
                    </strong>
                    .
                  </p>
                  <p className="mx-auto mt-2 max-w-md text-xs text-gray-500 dark:text-gray-400">
                    No further action is needed. The recruiter has been notified and will be in touch
                    if required.
                  </p>
                </div>
              )}

              {(state === 'ready' || state === 'submitting') && thread && (
                <>
                  {/* Candidate context panel */}
                  <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-950/40">
                    <div className="border-b border-gray-200 bg-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400">
                      From {thread.org_name}
                    </div>
                    <dl className="grid grid-cols-1 divide-y divide-gray-200 text-sm sm:grid-cols-2 sm:divide-x sm:divide-y-0 dark:divide-gray-800">
                      <div className="px-4 py-3">
                        <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Role
                        </dt>
                        <dd className="mt-0.5 font-semibold text-gray-900 dark:text-white">
                          {thread.job_title || '—'}
                        </dd>
                      </div>
                      <div className="px-4 py-3">
                        <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Candidate
                        </dt>
                        <dd className="mt-0.5 font-semibold text-gray-900 dark:text-white">
                          {thread.candidate_name || '—'}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                    What is your decision?
                  </h3>

                  <div className="space-y-2.5">
                    {CTA_OPTIONS.map(({ action, label, description, icon, selected: selClass, accent }) => {
                      const isSel = selected === action;
                      return (
                        <button
                          key={action}
                          type="button"
                          onClick={() => setSelected(action)}
                          className={[
                            'group flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-all',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                            isSel
                              ? selClass
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-gray-700 dark:hover:bg-gray-800/60',
                          ].join(' ')}
                        >
                          <span
                            className={[
                              'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                              isSel
                                ? accent
                                : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:group-hover:bg-gray-700',
                            ].join(' ')}
                          >
                            {icon}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold leading-tight">{label}</p>
                            <p className="mt-1 text-xs leading-snug opacity-80">{description}</p>
                          </div>
                          <span
                            aria-hidden
                            className={[
                              'mt-1 h-4 w-4 shrink-0 rounded-full border-2 transition-colors',
                              isSel
                                ? 'border-current bg-current'
                                : 'border-gray-300 dark:border-gray-600',
                            ].join(' ')}
                          />
                        </button>
                      );
                    })}
                  </div>

                  {(selected === 'query' || selected === 'reject') && (
                    <div className="mt-5">
                      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
                        {selected === 'query' ? (
                          <>
                            Your query <span className="text-rose-500">*</span>
                          </>
                        ) : (
                          'Reason (optional)'
                        )}
                      </label>
                      <textarea
                        className="block w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-900/40"
                        rows={3}
                        placeholder={
                          selected === 'query'
                            ? 'What would you like to know about this candidate?'
                            : 'Optional feedback to help the recruiter…'
                        }
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!selected || state === 'submitting' || (selected === 'query' && !note)}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {state === 'submitting' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                      </>
                    ) : (
                      'Submit response'
                    )}
                  </button>

                  <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Single-use, encrypted link · Your response is private
                  </p>
                </>
              )}

              {state === 'done' && (
                <div className="py-10 text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
                    <CheckCircle className="h-7 w-7 text-emerald-600 dark:text-emerald-300" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Response recorded
                  </h2>
                  <p className="mx-auto mt-2 max-w-md text-sm text-gray-600 dark:text-gray-300">
                    Thank you for your feedback. The recruiter has been notified and will be in
                    touch shortly.
                  </p>
                </div>
              )}
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-gray-500 dark:text-gray-500">
            Powered by{' '}
            <span className="font-semibold text-gray-700 dark:text-gray-300">
              GoGrabJob Business
            </span>
          </p>
        </div>
      </main>

      <B2BFooter />
    </div>
  );
}
